import { trackShare, type ContentType } from '@/lib/analytics';

/** What is being shared, for the GA4 `share` event. */
export interface ShareTarget {
  contentType: ContentType;
  itemId: string;
}

/**
 * Share a URL via the Web Share API, falling back to the clipboard.
 *
 * `url` is shared exactly as given. Never add UTM (or any tracking) parameters:
 * every tagged copy is another URL for the same page that crawlers pick up from
 * wherever it gets pasted, and GA4's `share` event already records the share.
 *
 * With a `target`, a completed share is reported to GA4 (`share`, method
 * `web_share` or `copy_link`); a dismissed share sheet reports nothing.
 *
 * Returns:
 *   'shared'  — the native share sheet handled it (or the user dismissed it).
 *   'copied'  — the URL was copied to the clipboard; show "Link copied!"
 *               feedback to the user.
 */
export async function shareUrl(
  title: string,
  url: string,
  target?: ShareTarget,
): Promise<'shared' | 'copied'> {
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, url });
      if (target) trackShare({ method: 'web_share', ...target });
      return 'shared';
    } catch (err) {
      // User dismissed the share sheet — nothing to do, no feedback needed.
      if (err instanceof DOMException && err.name === 'AbortError') {
        return 'shared';
      }
      // Share failed for another reason — fall through to the clipboard.
    }
  }
  await navigator.clipboard.writeText(url);
  if (target) trackShare({ method: 'copy_link', ...target });
  return 'copied';
}
